import {
    Claims,
    Entity,
    PagedResponse,
    QueryHookParams,
    SurgerySchedulerSurgeon,
    QueryType,
    IUseQuery,
    SurgerySchedulerSurgeonRequest,
    Person,
    QueryResult,
} from "../models";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { Endpoints, queryKeys } from "../api";
import { useAuth, usePermissions, useQueryParams } from "../contexts";
import { useMemo } from "react";
import { getQueryType, getRequestOptions } from "../helper-functions";

export interface ISurgerySchedulerSurgeonQuery extends Omit<IUseQuery<SurgerySchedulerSurgeon>, "updateMutation"> {
    mySurgeonsQuery: QueryResult<Person[]>;
}

export interface IUseSurgerySchedulerSurgeonQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "mySurgeons";
    includeInactive?: boolean;
}

export const useSurgerySchedulerSurgeonQuery = (
    params?: IUseSurgerySchedulerSurgeonQueryParams
): ISurgerySchedulerSurgeonQuery => {
    const { isAuthorized } = useAuth();
    const { get, post, del } = useAxios();
    const { queryParams, sortParams } = useQueryParams().getParamsApi(Entity.SurgerySchedulerSurgeons);
    const url = Endpoints.SurgerySchedulerSurgeon;
    const surgerySchedulerSurgeonId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { hasPermission } = usePermissions();

    const queryType = useMemo(() => {
        if (params?.queryType === "mySurgeons") return "mySurgeons";
        return getQueryType(surgerySchedulerSurgeonId, params?.queryType);
    }, [surgerySchedulerSurgeonId, params?.queryType]);

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<SurgerySchedulerSurgeon>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.surgerySchedulerSurgeons.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<SurgerySchedulerSurgeon>({ url: `${url}/${surgerySchedulerSurgeonId}`, config }),
        queryKey: queryKeys.surgerySchedulerSurgeons.detail(surgerySchedulerSurgeonId),
        placeholderData: undefined,
        enabled: hasPermission(Claims.ViewSurgerySchedulerSurgeon) && !!params?.id,
    });

    const mySurgeonsQuery = useGenericQuery({
        queryFn: (config) =>
            get<Person[]>({
                url: `${url}/my-surgeons`,
                config: { ...config, params: { includeInactive: params?.includeInactive } },
            }),
        queryKey: queryKeys.surgerySchedulerSurgeons.mySurgeons({ includeInactive: params?.includeInactive }),
        placeholderData: [],
        enabled: isAuthorized && queryType === "mySurgeons",
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<SurgerySchedulerSurgeon>) =>
            post<Partial<SurgerySchedulerSurgeonRequest>, SurgerySchedulerSurgeon>({ url, data }),
        queryKey: queryKeys.surgerySchedulerSurgeons.all(),
        keysToInvalidate: [queryKeys.surgerySchedulerSurgeons.lists(), queryKeys.surgerySchedulerSurgeons.pagedLists()],
        successToast: "Surgery Scheduler assigned to Surgeon",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: (id: number) => del({ url: `${url}/${id}` }),
        queryKey: queryKeys.surgerySchedulerSurgeons.detail(surgerySchedulerSurgeonId),
        keysToInvalidate: [queryKeys.surgerySchedulerSurgeons.lists(), queryKeys.surgerySchedulerSurgeons.pagedLists()],
        successToast: "Surgery Scheduler unassigned from Surgeon",
    });

    return useMemo<ISurgerySchedulerSurgeonQuery>(
        () => ({
            listQuery,
            detailQuery,
            mySurgeonsQuery,
            createMutation,
            deleteMutation,
        }),
        [listQuery, detailQuery, mySurgeonsQuery, createMutation, deleteMutation]
    );
};
